import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import type { CouchLoginResult } from '@cf-shared/couch-auth';
import { CoachClientSlot, CoachClientStatus } from '@cf-shared/data-model/coach';
import type { DatabaseWrapper, TransactionId } from 'services/database-wrapper';
import { ClientDashboardStatus, CoachActionItem, CoachDashboardRow, HandoverState } from 'services/coach-dashboard-helpers';
import { PresenceState } from 'services/coach-presence-helpers';

/** The part of the user database the demo-coach fixture reads and writes.
 *  Narrowed so a unit test can hand in a fake instead of a real wrapper. */
export type DemoCoachDatabase = Pick<DatabaseWrapper,
  'withDocWatcherAsync' | 'getByIdHeadAsync' | 'saveRecordAsync' | 'deleteRecordAsync'>;

/** What `installAsync` did to the persisted slot documents. */
export interface DemoCoachInstallResult {
  /** Demo slots from earlier builds (random-uuid ids) that were deleted. */
  legacyDeleted: number;
  /** Stable-id demo slots written by this install. */
  written: number;
  /** Stable-id demo slots already present and left alone. */
  keptExisting: number;
}

/**
 * The "demo coach" fixture behind the "Become demo coach" and "Remove
 * demo coach data" buttons on the TestUI page. It exists for visual QA
 * of `page-coach-settings` without an E2E run, and owns both halves of
 * that fixture:
 *
 *  - the persisted half: 6 `coach-client/demo-*` slot documents in the
 *    user's PouchDB, written by `installAsync` and deleted by
 *    `removeAsync`;
 *  - the in-memory half: canned dashboard rows, presence and a "stuck
 *    loading" marker. The Coach Dashboard normally hits remote CouchDB
 *    (per claimed client) to build each row + presence dot; for
 *    synthetic demo clients there is no real database to read, so the
 *    two read paths short-circuit through this seed.
 *
 * Lifecycle: the in-memory seed is populated on install and read on
 * every entry of `CoachSettingsPage`. It is cleared on a hard reload,
 * while the slot documents persist in PouchDB and render as Unknown
 * after a reload — the expected fallback for a stale demo.
 *
 * For any non-synthetic clientUid every read method returns "no seed"
 * so the production fetch path runs unchanged.
 */
@Injectable({ providedIn: 'root' })
export class CoachDemoSeedService {
  /** Stable id prefix for every demo `coach-client/*` document.
   *  Install writes by this prefix to keep re-taps idempotent. */
  static readonly DEMO_SLOT_ID_PREFIX = 'coach-client/demo-';
  /** Demo-slot detection markers consumed by `readDemoSlotDocsAsync`.
   *  Match on either of these so a legacy demo slot written under a
   *  random UUID id (before the stable-id refactor) is still cleaned
   *  up by the undo button. Real coach slots carry real Firebase uids
   *  and free-text nicknames, so the markers can't false-positive. */
  static readonly DEMO_UID_MARKER_PREFIX = 'demo-coach-uid-';
  static readonly DEMO_NICKNAME_MARKER = 'Demo · ';
  /** Stable synthetic client uids. The shared marker prefix above
   *  drives both the demo-detection scan and the seed-store keys. */
  static readonly DEMO_UID_ONTRACK = 'demo-coach-uid-ontrack';
  static readonly DEMO_UID_OFFTRACK = 'demo-coach-uid-offtrack';
  static readonly DEMO_UID_UNDETERMINED = 'demo-coach-uid-undetermined';
  static readonly DEMO_UID_UNKNOWN = 'demo-coach-uid-unknown';
  static readonly DEMO_UID_LOADING = 'demo-coach-uid-loading';

  private rowByUid = new Map<string, CoachDashboardRow>();
  private presenceByUid = new Map<string, PresenceState>();
  private loadingUids = new Set<string>();
  /** Pre-install `coachMaxSlots` value, captured so the undo button can
   *  restore the user's real grant. Undefined until the demo button is
   *  tapped at least once this session. After a reload the live
   *  `couch.coachMaxSlots` is back to the real CouchDB value, so the
   *  next install captures the right number again. */
  private savedCoachMaxSlots: number | undefined;

  /** True if any seed (row, presence, or loading marker) is registered for this uid. */
  isSynthetic(clientUid: string): boolean {
    return this.rowByUid.has(clientUid)
      || this.presenceByUid.has(clientUid)
      || this.loadingUids.has(clientUid);
  }

  /** A pre-built dashboard row for this synthetic uid, or undefined. */
  getSyntheticRow(clientUid: string): CoachDashboardRow | undefined {
    return this.rowByUid.get(clientUid);
  }

  /** When true, callers should leave the row absent from
   *  `dashboardByClientUid` so the page stays in loading-skeleton state.
   *  Models a real claim whose `computeRowAsync` is still in flight. */
  isSyntheticLoading(clientUid: string): boolean {
    return this.loadingUids.has(clientUid);
  }

  /** A pre-built presence state for this synthetic uid, or undefined. */
  getSyntheticPresence(clientUid: string): PresenceState | undefined {
    return this.presenceByUid.get(clientUid);
  }

  /** Install one batch of seed data. Idempotent across taps — repeated
   *  calls overwrite per uid. */
  install(seed: {
    rows?: CoachDashboardRow[];
    presence?: Array<[string, PresenceState]>;
    loadingUids?: string[];
  }): void {
    for (const row of seed.rows ?? []) this.rowByUid.set(row.clientUid, row);
    for (const [uid, state] of seed.presence ?? []) this.presenceByUid.set(uid, state);
    for (const uid of seed.loadingUids ?? []) this.loadingUids.add(uid);
  }

  /** Capture the user's real `coachMaxSlots` before the demo install
   *  overwrites it, so the undo path can restore it. No-op on repeat
   *  taps — only the first install in a session is the genuine value;
   *  later taps would capture the demo's own 5. */
  saveCoachMaxSlotsOnce(value: number): void {
    if (this.savedCoachMaxSlots === undefined) {
      this.savedCoachMaxSlots = value;
    }
  }

  /** The `coachMaxSlots` value to restore on undo, or undefined if no
   *  install has run this session. */
  getSavedCoachMaxSlots(): number | undefined {
    return this.savedCoachMaxSlots;
  }

  /** Wipe all in-memory seed state and forget the saved coachMaxSlots.
   *  The persisted slot documents are NOT touched — `removeAsync` deletes
   *  those before calling this. */
  clear(): void {
    this.rowByUid.clear();
    this.presenceByUid.clear();
    this.loadingUids.clear();
    this.savedCoachMaxSlots = undefined;
  }

  /**
   * Put the running app into "demo coach" mode:
   *
   *  1. Grants `coachMaxSlots = 5` in-memory on `couch`. The production
   *     grant goes via `tools/firebase-cmd.js coach:set-slots` + a
   *     refreshed CouchDB session — the client cannot call admin
   *     functions, so the resulting in-memory state is mirrored directly
   *     on the live session object.
   *  2. Seeds four synthetic dashboard rows (OnTrack / OffTrack with 2
   *     action items / Undetermined / Unknown), plus one "stuck loading"
   *     uid that keeps its row in the skeleton state.
   *  3. Converges PouchDB to the 6 demo slot documents — 4 claimed
   *     (mapped to the seeded uids), 1 claimed with the loading uid,
   *     1 pending invitation.
   *
   * Idempotent: stable `coach-client/demo-*` ids mean a repeat call
   * detects existing slots and re-seeds the in-memory store without
   * writing duplicate documents.
   */
  async installAsync(
    database: DemoCoachDatabase,
    couch: CouchLoginResult,
    transactionId: TransactionId,
  ): Promise<DemoCoachInstallResult> {
    // 1. capture the real coachMaxSlots first (no-op on repeat taps)
    //    so the undo path can restore the user's pre-demo grant.
    //    Plain assignment — `couch` is the live CouchLoginResult
    //    object held on the AuthService; the coach gates read this via
    //    effectiveCoachSlots (which falls back to `couch?.coachMaxSlots`).
    this.saveCoachMaxSlotsOnce(couch.coachMaxSlots ?? 0);
    couch.coachMaxSlots = 5;

    // 2. seed dashboard rows + presence + the loading marker.
    this.install(this.buildDemoSeedPayload());

    // 3. converge PouchDB to the 6 desired demo slot documents.
    //
    //    Two existence paths matter:
    //    a) Legacy slots: written by earlier builds of this button under
    //       random UUID ids (`coach-client/<uuid>`). Their `_id` is not in
    //       the desired stable set, so we delete them outright.
    //    b) Existing stable slots: written by a prior tap of THIS build
    //       (`coach-client/demo-<role>`). We must NOT delete-then-save
    //       these: a local deleteRecordAsync leaves the canonical entry
    //       in DatabaseWrapper's identity map (`documentCache`) with
    //       `_deleted: true`, because that cache is wiped only on
    //       pull-direction sync. A subsequent saveRecordAsync with a fresh
    //       object is folded into that canonical object, which copies the
    //       non-`_*` fields across but leaves `_deleted` set — so the save
    //       re-deletes the document. The demo content is fixed (only
    //       `createdAt` would change, and the dashboard doesn't sort by
    //       it), so skipping the rewrite is correct behaviour, not just a
    //       workaround.
    const slots = this.buildDemoSlots();
    const desiredIds = new Set(slots.map(s => s._id));
    const existing = await this.readDemoSlotDocsAsync(database);
    const legacyToDelete = existing.filter(s => !desiredIds.has(s._id));
    const alreadyPresentIds = new Set(
      existing.filter(s => desiredIds.has(s._id)).map(s => s._id));
    for (const legacySlot of legacyToDelete) {
      await database.deleteRecordAsync(legacySlot, transactionId);
    }
    let written = 0;
    for (const slot of slots) {
      // if a stable-id slot from a prior tap is already in PouchDB, leave
      // it alone (see the identity-map note above).
      if (alreadyPresentIds.has(slot._id)) continue;
      await database.saveRecordAsync(slot, transactionId);
      written++;
    }
    return { legacyDeleted: legacyToDelete.length, written, keptExisting: alreadyPresentIds.size };
  }

  /**
   * Tear down the demo coach state installed by `installAsync`: delete
   * every persisted demo slot from PouchDB, restore the real
   * `coachMaxSlots` grant captured at the first install, and wipe the
   * in-memory seed. Returns how many slot documents were deleted.
   */
  async removeAsync(
    database: DemoCoachDatabase,
    couch: CouchLoginResult | undefined,
    transactionId: TransactionId,
  ): Promise<number> {
    const existing = await this.readDemoSlotDocsAsync(database);
    for (const slot of existing) {
      await database.deleteRecordAsync(slot, transactionId);
    }

    // restore the pre-install coachMaxSlots, but ONLY if an install
    // captured a value earlier this session. After a reload `couch`
    // already holds the user's real Firebase grant — overwriting it
    // with 0 here would clobber a legitimate live grant until the
    // next CouchDB session refresh.
    const savedSlots = this.getSavedCoachMaxSlots();
    if (couch && savedSlots !== undefined) {
      couch.coachMaxSlots = savedSlots;
    }
    this.clear();
    return existing.length;
  }

  /** Read every demo `coach-client/*` document currently in PouchDB.
   *
   *  Used by both install (for the idempotency check) and undo. Scans
   *  the full `coach-client/` range and filters by two demo markers so
   *  legacy slots written by earlier taps of this button (when ids
   *  used `crypto.randomUUID()` instead of the stable `demo-<role>`
   *  suffix) still get detected and cleaned up:
   *    - `clientUid` starts with `demo-coach-uid-`, OR
   *    - `nickname` starts with the `Demo · ` marker (catches the
   *      Pending demo slot which has no clientUid).
   *  Both markers are unique to this demo button — real coach slots
   *  carry real Firebase uids and free-text nicknames. */
  private async readDemoSlotDocsAsync(database: DemoCoachDatabase): Promise<CoachClientSlot[]> {
    const all = await database.withDocWatcherAsync(
      watcher => database.getByIdHeadAsync('coach-client/', watcher)) as CoachClientSlot[];
    return all.filter(s =>
      s.clientUid?.startsWith(CoachDemoSeedService.DEMO_UID_MARKER_PREFIX)
      || s.nickname?.startsWith(CoachDemoSeedService.DEMO_NICKNAME_MARKER));
  }

  /** Build the in-memory seed payload — rows, presence, loading uids.
   *  Pure function of the current clock so action-item timestamps stay
   *  relative to "now" on each install. */
  private buildDemoSeedPayload(): {
    rows: CoachDashboardRow[];
    presence: Array<[string, PresenceState]>;
    loadingUids: string[];
  } {
    const nowUtc = DateTime.utc();
    const offTrackItems: CoachActionItem[] = [
      {
        type: 'checkup',
        logId: `log/${nowUtc.minus({ days: 1 }).toISO()}-weekly-checkup`,
        dateUtc: nowUtc.minus({ days: 1 }).toISO(),
      },
      {
        type: 'strengthTest',
        logId: `log/${nowUtc.minus({ days: 3 }).toISO()}-workout`,
        dateUtc: nowUtc.minus({ days: 3 }).toISO(),
      },
    ];
    // NotChecked throughout: these demo clients never went through a real claim,
    // so there is no handover to judge and the warning must not appear (#4817).
    const rows: CoachDashboardRow[] = [
      { clientUid: CoachDemoSeedService.DEMO_UID_ONTRACK, status: ClientDashboardStatus.OnTrack, items: [], handover: HandoverState.NotChecked },
      { clientUid: CoachDemoSeedService.DEMO_UID_OFFTRACK, status: ClientDashboardStatus.OffTrack, items: offTrackItems, handover: HandoverState.NotChecked },
      { clientUid: CoachDemoSeedService.DEMO_UID_UNDETERMINED, status: ClientDashboardStatus.Undetermined, items: [], handover: HandoverState.NotChecked },
      { clientUid: CoachDemoSeedService.DEMO_UID_UNKNOWN, status: 'Unknown', items: [], handover: HandoverState.NotChecked },
    ];
    const presence: Array<[string, PresenceState]> = [
      [CoachDemoSeedService.DEMO_UID_ONTRACK, { online: true, lastSeenUtc: nowUtc.toISO() }],
      [CoachDemoSeedService.DEMO_UID_OFFTRACK, { online: true, lastSeenUtc: nowUtc.toISO() }],
      [CoachDemoSeedService.DEMO_UID_UNDETERMINED, { online: false, lastSeenUtc: nowUtc.minus({ hours: 2 }).toISO() }],
      [CoachDemoSeedService.DEMO_UID_UNKNOWN, { online: false, lastSeenUtc: nowUtc.minus({ days: 2 }).toISO() }],
      [CoachDemoSeedService.DEMO_UID_LOADING, { online: true, lastSeenUtc: nowUtc.toISO() }],
    ];
    return { rows, presence, loadingUids: [CoachDemoSeedService.DEMO_UID_LOADING] };
  }

  /** Build the 6 slot documents using stable ids so re-taps don't
   *  duplicate. `createdAt` is generated relative to "now" to keep
   *  the dashboard's createdAt-ascending sort visually fresh. */
  private buildDemoSlots(): CoachClientSlot[] {
    const baseCreatedAt = DateTime.utc().minus({ minutes: 6 });
    const idPrefix = CoachDemoSeedService.DEMO_SLOT_ID_PREFIX;
    return [
      {
        _id: `${idPrefix}ontrack`,
        nickname: 'Demo · Alice (On Track)',
        status: CoachClientStatus.Claimed,
        clientUid: CoachDemoSeedService.DEMO_UID_ONTRACK,
        createdAt: baseCreatedAt.plus({ minutes: 1 }).toISO(),
      },
      {
        _id: `${idPrefix}offtrack`,
        nickname: 'Demo · Bob (Off Track, 2 items)',
        status: CoachClientStatus.Claimed,
        clientUid: CoachDemoSeedService.DEMO_UID_OFFTRACK,
        createdAt: baseCreatedAt.plus({ minutes: 2 }).toISO(),
      },
      {
        _id: `${idPrefix}undetermined`,
        nickname: 'Demo · Carla (Undetermined)',
        status: CoachClientStatus.Claimed,
        clientUid: CoachDemoSeedService.DEMO_UID_UNDETERMINED,
        createdAt: baseCreatedAt.plus({ minutes: 3 }).toISO(),
      },
      {
        _id: `${idPrefix}unknown`,
        nickname: 'Demo · Dan (Unknown)',
        status: CoachClientStatus.Claimed,
        clientUid: CoachDemoSeedService.DEMO_UID_UNKNOWN,
        createdAt: baseCreatedAt.plus({ minutes: 4 }).toISO(),
      },
      {
        _id: `${idPrefix}loading`,
        nickname: 'Demo · Eve (Loading…)',
        status: CoachClientStatus.Claimed,
        clientUid: CoachDemoSeedService.DEMO_UID_LOADING,
        createdAt: baseCreatedAt.plus({ minutes: 5 }).toISO(),
      },
      {
        _id: `${idPrefix}pending`,
        nickname: 'Demo · Fiona (Pending invite)',
        status: CoachClientStatus.Pending,
        createdAt: baseCreatedAt.plus({ minutes: 6 }).toISO(),
      },
    ];
  }
}
